import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import { useAppSelector } from "../../hooks/sharedHooks";

// ═══════════════════════════════════════════════════════════════
//  MIDAS Design Tokens (local subset)
// ═══════════════════════════════════════════════════════════════

const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldLight: "#F5D54A",
};

const FIRST_VISIT_KEY = "midas_chat_first_visit";

// ═══════════════════════════════════════════════════════════════
//  ChatBubble — slim vertical tab on the right edge
// ═══════════════════════════════════════════════════════════════

interface ChatBubbleProps {
  isPanelOpen: boolean;
  hasUnread: boolean;
  onClick: () => void;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  isPanelOpen,
  hasUnread,
  onClick,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const [entered, setEntered] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  // MIDAS-SCOPESTREAM: Read streaming state from popup buffer
  const isStreaming = useAppSelector(
    (state) => state.chatBot.threadBuffers["popup"]?.isLoading || false
  );

  useEffect(() => {
    const timer = setTimeout(() => setEntered(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const visited = localStorage.getItem(FIRST_VISIT_KEY);
    if (!visited) {
      setIsFirstVisit(true);
      localStorage.setItem(FIRST_VISIT_KEY, "true");
    }
  }, []);

  if (!entered) return null;

  // Phone: keep a small circular FAB
  if (isPhone) {
    return (
      <Box
        onClick={onClick}
        sx={{
          position: "fixed",
          bottom: "16px",
          right: "16px",
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${midas.gold} 0%, ${midas.goldDark} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 1400,
          boxShadow: "0 4px 16px rgba(212,160,23,0.3), 0 2px 6px rgba(0,0,0,0.1)",
          animation: "midasBubbleEntrance 0.5s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
      >
        {isPanelOpen ? (
          <CloseRounded sx={{ fontSize: "22px", color: "#FFF" }} />
        ) : (
          <MidasLogo size="sm" animated={isStreaming ? "pulse" : "none"} />
        )}
        {hasUnread && !isPanelOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: midas.gold,
              border: "2px solid #FFF",
              animation: "midasUnreadPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
            }}
          />
        )}
      </Box>
    );
  }

  // Desktop / Tablet: slim vertical tab on the right edge
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "fixed",
        bottom: "32px",
        right: 0,
        width: "36px",
        height: "120px",
        borderRadius: "10px 0 0 10px",
        background: `linear-gradient(180deg, ${midas.gold} 0%, ${midas.goldDark} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        cursor: "pointer",
        zIndex: 1400,
        boxShadow: "-3px 2px 12px rgba(212,160,23,0.25), -1px 1px 4px rgba(0,0,0,0.08)",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `midasBubbleEntrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both${
          isFirstVisit && !isPanelOpen
            ? ", midasPulse 2.5s ease-in-out 2s 3"
            : ""
        }`,
        "&:hover": {
          width: "42px",
          boxShadow: "-4px 4px 20px rgba(212,160,23,0.35), -2px 2px 8px rgba(0,0,0,0.1)",
        },
        "&:active": {
          width: "34px",
          transition: "width 0.1s ease",
        },
      }}
    >
      {isPanelOpen ? (
        <CloseRounded sx={{ fontSize: "20px", color: "#FFF" }} />
      ) : (
        <>
          <MidasLogo size="sm" animated={isStreaming ? "pulse" : "none"} style={{ width: 24, height: 24 }} />
          {/* Vertical "MIDAS" label */}
          {"MIDAS".split("").map((char, i) => (
            <Typography
              key={i}
              sx={{
                color: "#FFF",
                fontSize: "9px",
                fontWeight: 700,
                fontFamily: 'var(--font-sans)',
                lineHeight: 1,
                letterSpacing: "0.5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              {char}
            </Typography>
          ))}
        </>
      )}

      {/* Unread indicator */}
      {hasUnread && !isPanelOpen && (
        <Box
          sx={{
            position: "absolute",
            top: "4px",
            left: "-4px",
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: midas.goldLight,
            border: "2px solid #FFF",
            animation: "midasUnreadPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
          }}
        />
      )}
    </Box>
  );
};

export default ChatBubble;
